import { cloneDeep, isEmpty, isNil, omit } from 'lodash';
import { fromZonedTime } from 'date-fns-tz';
import { getBookableOfferedSolutions } from '../../components/booking/OfferedSolution.helpers';
import { buildRequestedPoint } from '../booking_helpers';

const SINGLETON_PAYMENT_METHOD_TYPES = ['cash', 'pos_payment', 'service_credits'];

export const defaultValueForDataType = dataType => {
  switch (dataType) {
    case 'boolean':
      return false;
    case 'string':
      return '';
    case 'integer':
      return 0;
    default:
      return null;
  }
};

const optionValues = options =>
  options.map(option => ({ slug: option.slug, value: defaultValueForDataType(option.data_type) }));

export const initialPassengerOptions = product =>
  product.passenger_options ? optionValues(product.passenger_options) : {};

export const defaultPassengerType = product =>
  product.passenger_types[0] ? product.passenger_types[0].slug : '';

export const newUninitializedPassenger = product => ({
  type: defaultPassengerType(product),
  first_name: null,
  last_name: null,
  options: initialPassengerOptions(product)
});

export const initialRideOptionValues = product =>
  product.product_ride_options ? optionValues(product.product_ride_options) : [];

// Returns a new collection with the value applied, or null when the slug is unknown. The collection is rebuilt because
// componentDidUpdate only picks the change up on a new object, not on a mutated one.
export const applyOptionValue = (options, slug, value) => {
  if (!options.find(option => option.slug === slug)) return null;

  return options.map(option => (option.slug === slug ? { ...option, value } : cloneDeep(option)));
};

// only let the singleton payment methods through here. The other payment methods will be added once the user is
// fetched
export const initialPaymentMethods = product =>
  product.provider.ride_payment_method_types
    .filter(type => SINGLETON_PAYMENT_METHOD_TYPES.includes(type))
    .map(type => ({ payment_method_type: type }));

export const rideNeedsPaymentMethod = ({ paymentMethods }) => paymentMethods.length > 0;

export const productSupportsPassengerNoteToDriver = product => product.supports_passenger_note_to_driver;

export const productRequiresFixedStation = product =>
  product.requires_fixed_station && product.fixed_stations.length > 0;

export const productSupportsPrebookingAssistance = product =>
  product.prebookable && product.supports_prebooking_ui_assistance;

export const productRequiresPassengerNames = product => product.requires_passenger_names_or_ticket;

export const allPassengersHavePublicTransportTicketOrNameProvided = passengers =>
  passengers.every(
    passenger =>
      isNil(passenger) ||
      passenger.options.find(option => option.slug === 'public_transport_ticket' && option.value === true) ||
      (!isEmpty(passenger.first_name) && !isEmpty(passenger.last_name))
  );

export const rideIsCreateable = ({ state, product }) =>
  !!state.origin &&
  !!state.destination &&
  !!state.passengers.length > 0 &&
  !!state.userId &&
  (!state.ride || state.ride.state === 'cancelled') &&
  (rideNeedsPaymentMethod(state) ? !!state.chosenPaymentMethod : true) &&
  (productRequiresPassengerNames(product)
    ? allPassengersHavePublicTransportTicketOrNameProvided(state.passengers)
    : true);

export const rideIsBookable = ({ ride }) =>
  ride && ride.state === 'ready' && getBookableOfferedSolutions(ride).length > 0;

export const originParams = ({ origin, nearbyMatchingMode }) =>
  nearbyMatchingMode.origin === true ? omit(origin, 'type', 'id', 'station_id') : origin;

export const destinationParams = ({ destination, nearbyMatchingMode }) =>
  nearbyMatchingMode.destination === true ? omit(destination, 'type', 'id', 'station_id') : destination;

const requestedTimeInProductZone = ({ state, product }) =>
  state.requestedTime ? fromZonedTime(state.requestedTime, product.timezone.identifier) : null;

export const buildWaypoints = ({ state, product }) => {
  const requestedTime = requestedTimeInProductZone({ state, product });

  return {
    origin: buildRequestedPoint({
      type: 'origin',
      params: originParams(state),
      requestedTimeForWaypoint: state.requestedTimeForWaypoint,
      requestedTime
    }),
    destination: buildRequestedPoint({
      type: 'destination',
      params: destinationParams(state),
      requestedTimeForWaypoint: state.requestedTimeForWaypoint,
      requestedTime
    })
  };
};
